import React, { useState } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { siteConfig } from '@data/site-config'
import { NavigationItem } from '@types/index'

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    if (activeDropdown) setActiveDropdown(null)
  }

  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label)
  }

  const renderNavigationItem = (item: NavigationItem, isMobile = false) => {
    const hasChildren = item.children && item.children.length > 0
    const isExternal = item.external

    if (hasChildren && !isMobile) {
      return (
        <div key={item.label} className="relative">
          <button
            onClick={() => toggleDropdown(item.label)}
            className="flex items-center space-x-1 px-3 py-2 text-gray-200 hover:text-blue-400 transition-colors duration-200"
            aria-expanded={activeDropdown === item.label}
            aria-haspopup="true"
          >
            <span>{item.label}</span>
            <ChevronDown 
              className={`w-4 h-4 transition-transform duration-200 ${
                activeDropdown === item.label ? 'rotate-180' : ''
              }`} 
            />
          </button>
          {activeDropdown === item.label && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-600 py-2 z-50">
              {item.children.map((child) => (
                <a
                  key={child.label}
                  href={child.href}
                  target={child.external ? '_blank' : undefined}
                  rel={child.external ? 'noopener noreferrer' : undefined}
                  className="block px-4 py-2 text-gray-200 hover:bg-gray-700 hover:text-blue-400 transition-colors duration-200"
                  onClick={() => setActiveDropdown(null)}
                >
                  {child.label}
                </a>
              ))}
            </div>
          )}
        </div>
      )
    }

    return (
      <a
        key={item.label}
        href={item.href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className={`
          px-3 py-2 text-gray-200 hover:text-blue-400 transition-colors duration-200
          ${isMobile ? 'block w-full text-left' : ''}
        `}
        onClick={() => {
          if (isMobile) {
            setIsMobileMenuOpen(false)
            setActiveDropdown(null)
          }
        }}
      >
        {item.label}
      </a>
    )
  }

  const renderMobileNavigationItem = (item: NavigationItem) => {
    const hasChildren = item.children && item.children.length > 0

    if (hasChildren) {
      return (
        <div key={item.label} className="space-y-1">
          <button
            onClick={() => toggleDropdown(item.label)}
            className="flex items-center justify-between w-full px-4 py-2 text-left text-gray-200 hover:bg-gray-700"
            aria-expanded={activeDropdown === item.label}
          >
            <span>{item.label}</span>
            <ChevronDown 
              className={`w-4 h-4 transition-transform duration-200 ${
                activeDropdown === item.label ? 'rotate-180' : ''
              }`} 
            />
          </button>
          {activeDropdown === item.label && (
            <div className="pl-6 space-y-1">
              {item.children.map((child) => (
                <a
                  key={child.label}
                  href={child.href}
                  target={child.external ? '_blank' : undefined}
                  rel={child.external ? 'noopener noreferrer' : undefined}
                  className="block px-4 py-2 text-gray-300 hover:text-blue-400 transition-colors duration-200"
                  onClick={() => {
                    setIsMobileMenuOpen(false)
                    setActiveDropdown(null)
                  }}
                >
                  {child.label}
                </a>
              ))}
            </div>
          )}
        </div>
      )
    }

    return renderNavigationItem(item, true)
  }

  return (
    <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg overflow-hidden">
                <img 
                  src="/hammer-icon.png" 
                  alt="HammerMUD"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-xl font-bold text-white">
                {siteConfig.title}
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {siteConfig.navigation.map(renderNavigationItem)}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href="/app"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Play Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-gray-300 hover:bg-gray-700 transition-colors duration-200"
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-800 border-t border-gray-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {siteConfig.navigation.map(renderMobileNavigationItem)}
            <div className="pt-4 border-t border-gray-700 mt-4">
              <a
                href="/app"
                className="block w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Play Now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header